import * as React from 'react';
import { Filter } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { useTailoredSupplyDomainsQuery } from '../../hooks';
import {
  tailoredSupplyPageSizes,
  type TailoredSupplyDomain,
  type TailoredSupplyDomainsFilter,
} from '../../model';
import { TailoredSupplyDomainsFilters } from './TailoredSupplyDomainsFilters';

interface TailoredSupplyDomainsTableProps {
  filter: TailoredSupplyDomainsFilter;
  selectedIds: ReadonlySet<string>;
  onFilterChange: (filter: TailoredSupplyDomainsFilter) => void;
  onSelectionChange: (items: TailoredSupplyDomain[], selected: boolean) => void;
}

export function TailoredSupplyDomainsTable({
  filter,
  selectedIds,
  onFilterChange,
  onSelectionChange,
}: TailoredSupplyDomainsTableProps) {
  const query = useTailoredSupplyDomainsQuery(filter);
  const items = React.useMemo(() => query.data?.content ?? [], [query.data?.content]);
  const selectedOnPage = items.filter((item) => selectedIds.has(item.id)).length;
  const allOnPageSelected = items.length > 0 && selectedOnPage === items.length;
  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;
  const [filtersOpen, setFiltersOpen] = React.useState(() => hasDomainsFilters(filter));
  const search = useDebouncedValue(
    filter.searchTerm,
    (searchTerm) => {
      onFilterChange({ ...filter, searchTerm, page: 0 });
    },
    650,
  );

  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<TailoredSupplyDomain>([
        {
          kind: DashboardDataTableColumnKind.Selection,
          id: 'selection',
          headerChecked: someOnPageSelected ? 'indeterminate' : allOnPageSelected,
          headerLabel: dashboardT('tailoredSupply.selectAllDomains'),
          onHeaderChange: (checked) => {
            onSelectionChange(items, checked);
          },
          checked: (domain) => selectedIds.has(domain.id),
          label: (domain) => dashboardT('tailoredSupply.selectDomain', { name: domain.url }),
          onChange: (domain, checked) => {
            onSelectionChange([domain], checked);
          },
        },
        {
          kind: DashboardDataTableColumnKind.Text,
          id: 'name',
          header: dashboardT('tailoredSupply.domain'),
          headerClassName: 'min-w-80 pl-4',
          className: 'min-w-80 pl-4 font-medium',
          value: (domain) => domain.url,
        },
        {
          kind: DashboardDataTableColumnKind.Text,
          id: 'publisher',
          header: dashboardT('tailoredSupply.publisher'),
          headerClassName: 'min-w-80',
          className: 'min-w-80',
          value: (domain) => domain.publisher?.name,
        },
      ]),
    [allOnPageSelected, items, onSelectionChange, selectedIds, someOnPageSelected],
  );

  return (
    <DashboardDataTable
      title={dashboardT('tailoredSupply.domains')}
      columns={columns}
      items={items}
      getRowKey={(domain) => domain.id}
      minWidthClassName="min-w-[720px]"
      loading={query.isLoading}
      loadingMessage={dashboardT('tailoredSupply.domainsLoading')}
      errorMessage={query.isError ? dashboardT('tailoredSupply.domainsLoadError') : undefined}
      emptyMessage={dashboardT('tailoredSupply.domainsEmpty')}
      toolbar={
        <div className="flex w-full min-w-0 items-center gap-2">
          <DashboardTableSearchInput
            className="min-w-0 flex-1"
            aria-label={dashboardT('tailoredSupply.domainsSearch')}
            value={search.draft}
            onChange={(event) => {
              search.setDraft(event.target.value);
            }}
          />
          <Button
            type="button"
            variant={filtersOpen ? 'secondary' : 'outline'}
            size="icon"
            aria-label={dashboardT(
              filtersOpen ? 'tailoredSupply.hideFilters' : 'tailoredSupply.showFilters',
            )}
            aria-pressed={filtersOpen}
            onClick={() => {
              setFiltersOpen((open) => !open);
            }}
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>
      }
      filterToolbar={
        filtersOpen ? (
          <TailoredSupplyDomainsFilters filter={filter} onChange={onFilterChange} />
        ) : undefined
      }
      pagination={{
        value: {
          page: filter.page,
          pageSize: filter.size,
          totalItems: query.data?.totalElements ?? 0,
        },
        pageSizes: tailoredSupplyPageSizes,
        disabled: query.isFetching,
        onChange: ({ page, pageSize }) => {
          onFilterChange({
            ...filter,
            page: pageSize === filter.size ? page : 0,
            size: pageSize,
          });
        },
      }}
    />
  );
}

function hasDomainsFilters(filter: TailoredSupplyDomainsFilter) {
  return filter.publisherIds.length > 0 || filter.iabCategoryIds.length > 0;
}
